import { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fetchGallery, fetchSmsQuota, sendSms } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Link2, ImageIcon, X, Infinity as InfinityIcon, Variable } from "lucide-react";

export default function SmsSendModal({ open, onOpenChange, recipients = [], preset = null }) {
  const { user } = useAuth();
  const userRecordId = user?.record_id || user?.user_record_id;

  const [message, setMessage] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const textareaRef = useRef(null);

  const withPhone = useMemo(() => recipients.filter((r) => r.phone), [recipients]);
  // Sample name for the live preview (first real recipient, else a placeholder).
  const previewName = (withPhone[0]?.first_name || "there").trim() || "there";

  function insertFirstName() {
    const token = "{{first_name}}";
    const el = textareaRef.current;
    if (!el) {
      setMessage((m) => m + token);
      return;
    }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  useEffect(() => {
    if (!open || !userRecordId) return;
    fetchSmsQuota(userRecordId).then(setQuota).catch(() => setQuota(null));
    setMessage(preset?.message || "");
    setLinkUrl(preset?.linkUrl || "");
    setImageUrl(preset?.imageUrl || "");
    setPickerOpen(false);
  }, [open, userRecordId, preset]);

  const loadGallery = async () => {
    setPickerOpen((v) => !v);
    if (gallery.length || !userRecordId) return;
    setLoadingGallery(true);
    try {
      const res = await fetchGallery(userRecordId);
      setGallery((res?.items || []).filter((i) => i.media === "image"));
    } catch {
      toast.error("Could not load gallery");
    } finally {
      setLoadingGallery(false);
    }
  };

  const fullBody = `${message}${linkUrl ? `\n${linkUrl}` : ""}`;
  const segments = Math.max(1, Math.ceil(fullBody.length / 160));

  const remainingLabel = quota?.unlimited
    ? "Unlimited (admin)"
    : quota
    ? `${quota.remaining} of ${quota.limit} left this month`
    : "…";

  const canSend =
    withPhone.length > 0 &&
    (message.trim() || imageUrl) &&
    !sending &&
    (quota?.unlimited || (quota?.remaining ?? 0) > 0);

  const handleSend = async () => {
    if (withPhone.length === 0) return toast.error("No recipients have a phone number");
    if (!message.trim() && !imageUrl) return toast.error("Add a message or an image");
    setSending(true);
    try {
      const res = await sendSms({
        userRecordId,
        message: message.trim(),
        linkUrl: linkUrl.trim(),
        imageUrl,
        recipients: withPhone.map((r) => ({
          id: r.id,
          phone: r.phone,
          first_name: r.first_name,
          last_name: r.last_name,
        })),
      });
      const parts = [`Sent ${res.sent} SMS`];
      if (res.failed) parts.push(`${res.failed} failed`);
      if (res.quota_blocked) parts.push(`${res.quota_blocked} blocked (monthly limit)`);
      toast[res.failed ? "warning" : "success"](parts.join(" · "));
      if (res.sent > 0) onOpenChange(false);
      if (userRecordId) fetchSmsQuota(userRecordId).then(setQuota).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send SMS");
    } finally {
      setSending(false);
    }
  };

  const skipped = recipients.length - withPhone.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sms-send-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={18} className="text-emerald-600" />
            Text {withPhone.length} {withPhone.length === 1 ? "contact" : "contacts"}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            {quota?.unlimited && <InfinityIcon size={13} className="text-emerald-600" />}
            <span data-testid="sms-quota-label">{remainingLabel}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {skipped > 0 && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2">
              {skipped} contact{skipped === 1 ? "" : "s"} skipped — no phone number on file.
            </p>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Message</Label>
              <button
                type="button"
                onClick={insertFirstName}
                className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2.5 py-1 text-[11px] font-medium hover:bg-violet-200 transition-colors"
                data-testid="sms-insert-firstname"
              >
                <Variable size={11} /> Insert first name
              </button>
            </div>
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Write your text message… e.g. Hi {{first_name}}, ..."
              data-testid="sms-message-input"
              className="mt-1 resize-none"
            />
            {message.includes("{{first_name}}") && (
              <p className="mt-1 text-[11px] text-zinc-500" data-testid="sms-firstname-preview">
                Preview: {message.replace(/\{\{\s*first_name\s*\}\}/gi, previewName)}
              </p>
            )}
            <div className="mt-1 flex justify-between text-[11px] text-zinc-400">
              <span>{fullBody.length} chars</span>
              <span>{segments} SMS segment{segments === 1 ? "" : "s"} each</span>
            </div>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Link2 size={13} /> Link URL (optional)
            </Label>
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://orevital.com/…"
              data-testid="sms-link-input"
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs flex items-center gap-1.5">
                <ImageIcon size={13} /> Image (MMS, optional)
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadGallery}
                data-testid="sms-gallery-toggle"
              >
                {imageUrl ? "Change image" : "Add from gallery"}
              </Button>
            </div>

            {imageUrl && (
              <div className="mt-2 relative inline-block">
                <img
                  src={imageUrl}
                  alt="attachment"
                  className="h-24 rounded-lg border border-zinc-200 object-cover"
                />
                <button
                  type="button"
                  onClick={() => setImageUrl("")}
                  className="absolute -top-2 -right-2 bg-zinc-900 text-white rounded-full p-1"
                  data-testid="sms-image-remove"
                  aria-label="Remove image"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {pickerOpen && (
              <div className="mt-2 border border-zinc-200 rounded-lg p-2 max-h-44 overflow-y-auto">
                {loadingGallery ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="animate-spin text-zinc-400" size={18} />
                  </div>
                ) : gallery.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-4">No images in gallery</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {gallery.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => {
                          setImageUrl(g.url);
                          setPickerOpen(false);
                        }}
                        className={`aspect-square rounded-md overflow-hidden border-2 ${
                          imageUrl === g.url ? "border-emerald-500" : "border-transparent"
                        }`}
                        data-testid={`sms-gallery-img-${g.key}`}
                      >
                        <img src={g.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="sms-send-btn"
          >
            {sending ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Send size={16} className="mr-2" />
            )}
            Send to {withPhone.length} {withPhone.length === 1 ? "contact" : "contacts"}
          </Button>

          {!quota?.unlimited && quota && quota.remaining <= 0 && (
            <p className="text-xs text-red-500 text-center">
              You've reached your {quota.limit} SMS/month limit. Resets next month.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
